import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import { FaSteam } from "react-icons/fa";

import { FollowButton } from "../../../Components/Button";
import { ESuggestedFollowReason, ISuggestedFollower } from "../@types";
import { useMutateFollowers } from "../../../Hooks/useMutateFollowers";
import { TMutateFollowers } from "../../../Hooks/@types";
import { AllstarModalState } from "../../../State/modals";
import { ModalType } from "../../../@types";
import { SuggestedFollowing } from "MVVM/State/suggestedFollowing";
import { useUser } from "MVVM/Hooks/useUser";
import { SUGGESTED_REASON_CONFIG } from "./reasonConfig";
import { SuggestionBadge } from "../../../Components/SuggestionBadge";
import { Avatar } from "MVVM/Components/AvatarV2";

interface ISuggestedItemProps {
  user: ISuggestedFollower;
  reason: ESuggestedFollowReason;
}

const SuggestedItem = ({ user, reason }: ISuggestedItemProps) => {
  const { allstarUser } = useUser();
  const [suggestedFollowing, setSuggestedFollowing] =
    useRecoilState(SuggestedFollowing);
  const history = useHistory();
  const { mutateFollowers } = useMutateFollowers();

  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const followUnfollow = (data: TMutateFollowers) => {
    setSuggestedFollowing({
      ids: [...suggestedFollowing.ids, data?.variables?.userToFollow],
    });

    return mutateFollowers(data);
  };
  const setFollowModal = ({
    displayName,
    avatar,
  }: {
    displayName: string;
    avatar: string;
  }) => {
    setAllstarModalState({
      ...allstarModalState,
      isOpen: ModalType.Signup,
      data: {
        target: displayName,
        avatar: avatar,
        action: "follow",
      },
    });
  };

  const suggestionItem =
    SUGGESTED_REASON_CONFIG[reason] || SUGGESTED_REASON_CONFIG["Unspecified"];

  const handleUserClick = () => {
    if (!user?.username) return;

    const location =
      !user.username || user.username === "User"
        ? `/profile?user=${user._id}`
        : `/u/${user.username}`;

    history.push(location);

    window.scroll({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  const username =
    (user.username !== "User" ? user.username : user?.steam?.personaname) ??
    "User";

  const isFollowBackButton =
    suggestionItem === SUGGESTED_REASON_CONFIG["Follow Back"];

  return (
    <SuggestedItemWrapper>
      <SuggestedItemContainer>
        <Banner />
        <Content>
          <AvatarClickable onClick={handleUserClick}>
            <AvatarRing>
              <Avatar
                userGroup={user.userGroup?.type || "FREE"}
                rank={user.rank}
                src={user.avatar}
                size="lg"
              />
            </AvatarRing>
          </AvatarClickable>

          <TextWrapper onClick={handleUserClick}>
            <UsernameText>{username}</UsernameText>
            <SteamUsername>
              {user?.steam?.personaname ? (
                <>
                  <FaSteam />
                  <SteamUsernameText>
                    {user.steam.personaname}
                  </SteamUsernameText>
                </>
              ) : (
                <>&nbsp;</>
              )}
            </SteamUsername>
          </TextWrapper>

          <ReasonRow>
            <SuggestionBadge reason={reason} showTooltip={false} />
          </ReasonRow>

          <FollowButtonRow>
            <FollowButton
              followBack={isFollowBackButton}
              size="tiny"
              flex={true}
              openModal={true}
              isModal={false}
              followUnfollow={followUnfollow}
              setFollowModal={setFollowModal}
              userId={user._id}
              allstarUser={allstarUser}
              displayName={user.username}
              avatar={user.avatar}
              trackingInfo={{
                avatarPresent: !!user.avatar,
                discordPresent: !!user?.discord?.id,
                followingCount: allstarUser?.user?.followingCount,
                reason: user.reason,
              }}
            />
          </FollowButtonRow>
        </Content>
      </SuggestedItemContainer>
    </SuggestedItemWrapper>
  );
};

const AvatarClickable = styled(Box)`
  cursor: pointer;
`;

const SuggestedItemWrapper = styled(Box)`
  width: 196px;
`;

const SuggestedItemContainer = styled(Flex)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  transition:
    border-color 0.18s ease,
    transform 0.18s ease;

  &:hover {
    border-color: rgba(77, 189, 148, 0.45);
    transform: translateY(-2px);
  }
`;

const Banner = styled(Box)`
  height: 60px;
  width: 100%;
  background: linear-gradient(
    135deg,
    rgba(77, 189, 148, 0.45) 0%,
    rgba(37, 58, 73, 0.2) 55%,
    rgba(24, 37, 45, 0) 100%
  );
`;

const Content = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 6px;
  margin-top: -34px;
  padding: 0 16px 18px;
`;

const AvatarRing = styled(Box)`
  border-radius: 50%;
  border: 3px solid ${({ theme }) => theme.colors.darkerNed};
  background: ${({ theme }) => theme.colors.darkerNed};
  line-height: 0;
`;

const TextWrapper = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  flex-direction: column;
  gap: 3px;
  justify-content: center;
  margin-top: 6px;
  max-width: 100%;
  outline: none;
`;

const UsernameText = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  max-width: 160px;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const SteamUsername = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  justify-content: center;
  max-width: 160px;
  width: 100%;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const SteamUsernameText = styled.span`
  max-width: calc(100% - 16px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReasonRow = styled(Box)`
  margin-top: 4px;
`;

const FollowButtonRow = styled(Box)`
  display: flex;
  justify-content: center;
  margin-top: 12px;
  width: 100%;
`;

export { SuggestedItem };
